import React from 'react';
import { Icon } from '../icons.jsx';
import { fmtMoney, fmtDateFull } from '../components.jsx';
import { notify } from '../modals.jsx';

const { useState, useMemo, useRef, useCallback } = React;

// =========================================================
// Radar / Bandeja de oportunidades (demo)
// Cribado: importar Excel de Infonalia → revisar → preseleccionar
// =========================================================
function daysLeft(date) {
  if (!date) return null;
  const d = new Date(date);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.round((d - today) / 86_400_000);
}

// Scorecard bid/no-bid heurístico (demo): puntúa encaje básico
function scoreOpportunity(o) {
  const dl = daysLeft(o.deadline) ?? 0;
  const fit = /consultor|emprend|acelera|innova|formaci|economía|impacto|liderazgo/i.test(o.title) ? 4 : 2;
  const budgetScore = o.budget >= 200_000 ? 4 : o.budget >= 50_000 ? 3 : 1;
  const timeScore = dl >= 20 ? 4 : dl >= 10 ? 3 : dl >= 0 ? 1 : 0;
  const competitionScore = o.budget < 60_000 ? 2 : 3;
  const total = fit + budgetScore + timeScore + competitionScore; // /16
  return { fit, budgetScore, timeScore, competitionScore, total, pct: Math.round((total / 16) * 100) };
}

function recommendation(pct) {
  if (pct >= 70) return { label: 'Go', sem: 'sem-success' };
  if (pct >= 45) return { label: 'Revisar', sem: 'sem-warning' };
  return { label: 'No-Go', sem: 'sem-danger' };
}

const STATUS_META = {
  new:         { label: 'Nuevas',          sem: 'sem-info' },
  preselected: { label: 'Preseleccionadas', sem: 'sem-success' },
  discarded:   { label: 'Descartadas',     sem: 'sem-danger' },
};

export function RadarScreen({ openBid }) {
  const seed = window.MockData.OPPORTUNITIES || [];
  const [items, setItems] = useState(() => seed.map((o) => ({ ...o })));
  const [tab, setTab] = useState('new');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(() => new Set());
  const [importOpen, setImportOpen] = useState(false);

  const counts = useMemo(() => ({
    all: items.length,
    new: items.filter((o) => o.status === 'new').length,
    preselected: items.filter((o) => o.status === 'preselected').length,
    discarded: items.filter((o) => o.status === 'discarded').length,
  }), [items]);

  const list = items.filter((o) => {
    if (tab !== 'all' && o.status !== tab) return false;
    if (search && !((o.title || '') + (o.entity || '') + (o.code || '') + (o.region || '')).toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const setStatus = useCallback((ids, status) => {
    setItems((prev) => prev.map((o) => (ids.includes(o.id) ? { ...o, status } : o)));
    setSelected(new Set());
  }, []);

  const preselect = (ids) => {
    setStatus(ids, 'preselected');
    notify(`${ids.length} oportunidad${ids.length === 1 ? '' : 'es'} preseleccionada${ids.length === 1 ? '' : 's'} · pasa a Avistada`, 'CheckCircle');
  };
  const discard = (ids) => {
    setStatus(ids, 'discarded');
    notify(`${ids.length} oportunidad${ids.length === 1 ? '' : 'es'} descartada${ids.length === 1 ? '' : 's'}`, 'Trash');
  };

  const toggle = (id) => setSelected((prev) => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });
  const allVisibleSelected = list.length > 0 && list.every((o) => selected.has(o.id));
  const toggleAll = () => setSelected(() => (allVisibleSelected ? new Set() : new Set(list.map((o) => o.id))));

  const tabs = [
    { id: 'new', label: 'Nuevas', count: counts.new },
    { id: 'preselected', label: 'Preseleccionadas', count: counts.preselected },
    { id: 'discarded', label: 'Descartadas', count: counts.discarded },
    { id: 'all', label: 'Todas', count: counts.all },
  ];
  const selArr = [...selected];

  return (
    <div className="viewport-inner fade-in">
      <div className="page-head">
        <div>
          <div className="page-eyebrow">Cribado · Bandeja de entrada</div>
          <h1 className="page-title">Radar de oportunidades</h1>
          <p className="muted" style={{ fontSize: 14, marginTop: 2 }}>Oportunidades recibidas de Infonalia. Revisa, puntúa y preselecciona las que merece la pena analizar.</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-primary" onClick={() => setImportOpen(true)}><Icon.Upload size={14} /> Importar Excel</button>
        </div>
      </div>

      {/* KPIs del embudo */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 16 }}>
        <FunnelStat icon="Compass" label="En radar" value={counts.new} tone="sem-info" />
        <FunnelStat icon="CheckCircle" label="Preseleccionadas" value={counts.preselected} tone="sem-success" />
        <FunnelStat icon="Trash" label="Descartadas" value={counts.discarded} tone="sem-danger" />
        <FunnelStat icon="Target" label="Tasa de paso" value={`${counts.all ? Math.round((counts.preselected / counts.all) * 100) : 0}%`} tone="sem-violet" />
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
        <div className="tabs" style={{ flex: '0 1 auto' }}>
          {tabs.map((t) => (
            <div key={t.id} className={`tab ${tab === t.id ? 'active' : ''}`} onClick={() => setTab(t.id)}>
              {t.label} <span style={{ marginLeft: 6, fontSize: 11, color: 'var(--text-4)' }}>{t.count}</span>
            </div>
          ))}
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', border: '1px solid var(--line-2)', borderRadius: 8, background: 'var(--bg-1)' }}>
          <Icon.Search size={13} />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar organismo, objeto, región…" style={{ background: 'transparent', border: 0, fontSize: 12.5, color: 'var(--text-1)', width: 240 }} />
        </div>
      </div>

      {/* Barra de acciones en lote */}
      {selArr.length > 0 && (
        <div className="scale-in" style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', marginBottom: 12, borderRadius: 10, background: 'var(--bg-2)', border: '1px solid var(--line-2)' }}>
          <span style={{ fontSize: 13, color: 'var(--text-2)' }}><strong>{selArr.length}</strong> seleccionada{selArr.length === 1 ? '' : 's'}</span>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
            <button className="btn btn-outline" onClick={() => notify('Responsable asignado (demo)', 'Users')}><Icon.Users size={13} /> Asignar</button>
            <button className="btn btn-outline" onClick={() => discard(selArr)}><Icon.Trash size={13} /> Descartar</button>
            <button className="btn btn-primary" onClick={() => preselect(selArr)}><Icon.CheckCircle size={13} /> Preseleccionar</button>
          </div>
        </div>
      )}

      <div className="card" style={{ overflow: 'visible' }}>
        <table className="tbl">
          <thead>
            <tr>
              <th style={{ width: 36 }}><input type="checkbox" checked={allVisibleSelected} onChange={toggleAll} /></th>
              <th>Objeto del contrato</th>
              <th style={{ width: 120 }}>Región</th>
              <th style={{ width: 120 }}>Presupuesto</th>
              <th style={{ width: 130 }}>Plazo</th>
              <th style={{ width: 150 }}>Bid / No-Bid</th>
              <th style={{ width: 130 }}></th>
            </tr>
          </thead>
          <tbody>
            {list.map((o) => (
              <OppRow key={o.id} opp={o} checked={selected.has(o.id)} onToggle={() => toggle(o.id)} onPreselect={() => preselect([o.id])} onDiscard={() => discard([o.id])} />
            ))}
          </tbody>
        </table>
        {list.length === 0 && (
          <div style={{ padding: 60, textAlign: 'center', color: 'var(--text-3)' }}>
            <Icon.Compass size={28} />
            <div style={{ marginTop: 10 }}>No hay oportunidades en este filtro.</div>
          </div>
        )}
      </div>

      <ImportExcelModal open={importOpen} onClose={() => setImportOpen(false)} />
    </div>
  );
}

function FunnelStat({ icon, label, value, tone }) {
  const Ico = Icon[icon];
  return (
    <div className="card" style={{ padding: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
      <span className={`sem ${tone}`} style={{ width: 36, height: 36, borderRadius: 10, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}><Ico size={17} /></span>
      <div>
        <div style={{ fontSize: 22, fontWeight: 600, color: 'var(--text-1)', lineHeight: 1 }}>{value}</div>
        <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 3 }}>{label}</div>
      </div>
    </div>
  );
}

function OppRow({ opp, checked, onToggle, onPreselect, onDiscard }) {
  const sc = scoreOpportunity(opp);
  const rec = recommendation(sc.pct);
  const dl = daysLeft(opp.deadline);
  return (
    <tr style={{ background: checked ? 'var(--bg-active)' : undefined }}>
      <td onClick={(e) => e.stopPropagation()}><input type="checkbox" checked={checked} onChange={onToggle} /></td>
      <td>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3 }}>
          <span className="chip" style={{ fontSize: 10 }}>{opp.type}</span>
          <span style={{ fontSize: 11, color: 'var(--text-3)' }}>{opp.entityShort}</span>
          {opp.status === 'preselected' && <span className="sem sem-success" style={{ fontSize: 10 }}>Preseleccionada</span>}
          {opp.status === 'discarded' && <span className="sem sem-danger" style={{ fontSize: 10 }}>Descartada</span>}
        </div>
        <div style={{ fontSize: 13.5, color: 'var(--text-1)' }}>{opp.title}</div>
        <div className="font-mono" style={{ fontSize: 10.5, color: 'var(--text-4)', marginTop: 2 }}>{opp.code} · {opp.cpv}</div>
      </td>
      <td style={{ fontSize: 12.5, color: 'var(--text-2)' }}>{opp.region}</td>
      <td className="font-mono" style={{ fontSize: 12.5 }}>{fmtMoney(opp.budget)}</td>
      <td>
        <div style={{ fontSize: 12 }}>{fmtDateFull(opp.deadline)}</div>
        <div style={{ fontSize: 11, color: dl != null && dl < 10 ? 'var(--sem-warning)' : 'var(--text-3)', marginTop: 1 }}>
          {dl != null ? (dl >= 0 ? `en ${dl} días` : `vencida`) : '—'}
        </div>
      </td>
      <td>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span className={`sem ${rec.sem}`} style={{ fontSize: 11 }}>{rec.label}</span>
          <span style={{ fontSize: 11, color: 'var(--text-3)' }}>{sc.pct}</span>
        </div>
        <div style={{ height: 4, borderRadius: 99, background: 'var(--line-1)', marginTop: 5, overflow: 'hidden' }}>
          <div style={{ width: `${sc.pct}%`, height: '100%', background: `var(--${rec.sem.replace('sem-', 'sem-')})` }} />
        </div>
      </td>
      <td onClick={(e) => e.stopPropagation()}>
        {opp.status !== 'discarded' && opp.status !== 'preselected' && (
          <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
            <button className="btn btn-ghost btn-icon" title="Descartar" onClick={onDiscard}><Icon.Trash size={13} /></button>
            <button className="btn btn-primary" style={{ padding: '5px 10px', fontSize: 12 }} onClick={onPreselect}>Preseleccionar</button>
          </div>
        )}
        {opp.status === 'preselected' && <a className="chip" href={opp.url} target="_blank" rel="noreferrer" style={{ textDecoration: 'none' }}><Icon.Globe size={11} /> Ver pliego</a>}
      </td>
    </tr>
  );
}

// ---------- Modal: importar Excel de Infonalia (simulado) ----------
const COLUMN_MAP = [
  ['Objeto', 'Objeto del contrato'],
  ['OrganismoConvocante', 'Entidad'],
  ['Nº Expediente', 'Código'],
  ['Presupuesto', 'Presupuesto base'],
  ['PlazoPresentacionFecha', 'Plazo de presentación'],
  ['LugarEjecucion', 'Región'],
  ['InformacionWeb', 'Enlace al pliego'],
  ['TipoDato', 'Tipo de procedimiento'],
];

function ImportExcelModal({ open, onClose }) {
  const [file, setFile] = useState(null);
  const [drag, setDrag] = useState(false);
  const [phase, setPhase] = useState('drop'); // drop | preview | done
  const inputRef = useRef(null);

  if (!open) return null;

  const accept = (f) => { if (f) { setFile(f); setPhase('preview'); } };
  const reset = () => { setFile(null); setPhase('drop'); };
  const close = () => { reset(); onClose(); };
  const confirm = () => {
    setPhase('done');
    notify(`${(window.MockData.OPPORTUNITIES || []).length} oportunidades importadas a Radar (demo)`, 'CheckCircle');
    setTimeout(close, 900);
  };

  return (
    <div className="modal-overlay" onClick={close} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)', zIndex: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div className="card scale-in" onClick={(e) => e.stopPropagation()} style={{ width: 560, maxWidth: '100%', padding: 0, overflow: 'hidden' }}>
        <div style={{ padding: '18px 22px', borderBottom: '1px solid var(--line-1)', display: 'flex', alignItems: 'center', gap: 10 }}>
          <span className="sem sem-violet" style={{ width: 34, height: 34, borderRadius: 9, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}><Icon.Upload size={16} /></span>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 15, fontWeight: 600 }}>Importar oportunidades de Infonalia</div>
            <div style={{ fontSize: 12, color: 'var(--text-3)' }}>Sube el Excel diario para añadirlo al radar.</div>
          </div>
          <button className="btn btn-ghost btn-icon" onClick={close}><Icon.Plus size={15} style={{ transform: 'rotate(45deg)' }} /></button>
        </div>

        <div style={{ padding: 22 }}>
          {phase === 'drop' && (
            <div
              onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
              onDragLeave={() => setDrag(false)}
              onDrop={(e) => { e.preventDefault(); setDrag(false); accept(e.dataTransfer.files?.[0]); }}
              onClick={() => inputRef.current?.click()}
              style={{ border: `2px dashed ${drag ? 'var(--primary)' : 'var(--line-2)'}`, borderRadius: 12, padding: '40px 20px', textAlign: 'center', cursor: 'pointer', background: drag ? 'var(--bg-active)' : 'var(--bg-1)', transition: 'all 120ms' }}
            >
              <Icon.Upload size={26} style={{ color: 'var(--text-3)' }} />
              <div style={{ marginTop: 12, fontSize: 14, color: 'var(--text-1)' }}>Arrastra el Excel aquí o haz clic para seleccionar</div>
              <div style={{ marginTop: 4, fontSize: 12, color: 'var(--text-3)' }}>Formato Infonalia · .xlsx</div>
              <input ref={inputRef} type="file" accept=".xlsx,.xls,.csv" style={{ display: 'none' }} onChange={(e) => accept(e.target.files?.[0])} />
            </div>
          )}

          {phase !== 'drop' && (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 9, background: 'var(--bg-2)', marginBottom: 16 }}>
                <Icon.FileText size={16} style={{ color: 'var(--sem-success)' }} />
                <span style={{ fontSize: 13, flex: 1 }}>{file?.name || '20260618_Licitaciones.xlsx'}</span>
                <span className="sem sem-success" style={{ fontSize: 11 }}>Detectado</span>
              </div>
              <div className="section-title" style={{ marginBottom: 8 }}>Mapeo de columnas</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 220, overflow: 'auto' }}>
                {COLUMN_MAP.map(([from, to]) => (
                  <div key={from} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 12.5 }}>
                    <span className="font-mono" style={{ flex: 1, color: 'var(--text-3)' }}>{from}</span>
                    <Icon.ArrowRight size={13} style={{ color: 'var(--text-4)' }} />
                    <span style={{ flex: 1, color: 'var(--text-1)' }}>{to}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        <div style={{ padding: '14px 22px', borderTop: '1px solid var(--line-1)', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button className="btn btn-outline" onClick={close}>Cancelar</button>
          <button className="btn btn-primary" disabled={phase === 'drop'} onClick={confirm}><Icon.CheckCircle size={14} /> Importar al radar</button>
        </div>
      </div>
    </div>
  );
}
